/* eslint-disable @typescript-eslint/no-explicit-any */

import { ComponentType, JSX, ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { components, FilterOptionOption, GroupBase, NoticeProps } from "react-select"
import { TypedUseLazyQuery } from "@reduxjs/toolkit/query/react"
import classNames from "classnames"
import { debounce, isEqual } from "lodash"

import { useAppDispatch } from "store/hooks"
import { setCurrentZip } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"

import { convertDataToOptions, convertStringArrayToOptions } from "utils/convertData"
import { usePrevious } from "utils/usePrevious"

import { SelectStatic } from "../SelectStatic"

import styles from "./index.module.scss"

export type SearchFnType = (option: FilterOptionOption<UITypes.Option>, inputValue: string) => boolean

interface Props {
  dataTestId?: string
  label?: string
  placeholder?: string
  name?: string
  values: UITypes.Option[]
  errorMessage?: string
  valueName: string
  labelName: string
  searchFieldName?: string
  className?: string
  queryParams?: UITypes.Params
  requestOnSearchOnly?: boolean
  isSearchable?: boolean
  searchFn?: SearchFnType
  onChange?: (value: UITypes.Option[]) => void
  addCustomOptionRenderer?: (searchValue: string) => ReactNode
  useLazyQuery: TypedUseLazyQuery<any, any, any>
  optionRenderer?: JSX.Element
  responseType?: "stringArray" | "objectArray"
  backspaceDelete?: boolean
  disabled?: boolean
  noOptionsMessage?: ComponentType<NoticeProps<UITypes.Option, boolean, GroupBase<UITypes.Option>>>
}

export const SelectDynamic = ({
  dataTestId,
  label,
  placeholder,
  name,
  values = [],
  errorMessage,
  valueName,
  labelName,
  className,
  searchFieldName = "name",
  backspaceDelete = false,
  queryParams = {},
  disabled = false,
  noOptionsMessage,
  requestOnSearchOnly,
  isSearchable = true,
  onChange,
  searchFn,
  useLazyQuery,
  addCustomOptionRenderer,
  responseType = "objectArray",
}: Props) => {
  if (!useLazyQuery || typeof useLazyQuery !== "function" || !useLazyQuery()?.length)
    // Additional checking for useLazyQuery
    return <div className={styles.errorText}>Error in &#34;SelectDynamic&#34; component</div>

  const [triggerGetData, { isFetching, data }] = useLazyQuery()
  const [searchValue, setSearchValue] = useState("")
  const [permanentSearchValue, setPermanentSearchValue] = useState("")
  const prevSearchValue = usePrevious(searchValue)
  const prevQueryParams = usePrevious(queryParams)
  const dispatch = useAppDispatch()

  const onSetSearchValue = (inputValue: string) => {
    setSearchValue(inputValue)
  }

  const setDebounceSearchValueRef = useRef(debounce(onSetSearchValue, 600))

  const onSearchChange = (option: FilterOptionOption<UITypes.Option>, inputValue: string) => {
    return searchFn?.(option, inputValue) ?? true
  }

  const handleInputChange = (inputValue: string) => {
    setPermanentSearchValue(inputValue)
    setDebounceSearchValueRef.current(inputValue)
  }

  // FOR ZIP SELECT ONLY

  const filterCityStateBasedOnZip = (options: UITypes.Option[], resData: any) => {
    if (!resData?.data?.length) return
    const value = options[0]?.value
    const result = resData?.data?.filter((item: ApiTypes.Model.Zip) => Number(value) === item.id)
    if (result?.length) {
      dispatch(setCurrentZip(result[0]))
    }
  }

  const onSelectChange = (options: UITypes.Option[]) => {
    if (name === "zip") {
      filterCityStateBasedOnZip(options, data)
    }
    onChange?.(options)
  }

  useEffect(() => {
    if (requestOnSearchOnly && searchValue === "") {
      return
    }

    if (searchValue !== prevSearchValue || !isEqual(prevQueryParams, queryParams)) {
      triggerGetData(searchValue === "" ? queryParams : { [searchFieldName]: searchValue, ...queryParams })
    }
  }, [searchValue, queryParams, prevSearchValue])

  const renderCustomOption = (data: any) => {
    if (
      addCustomOptionRenderer &&
      data.options.every(
        (option: any) =>
          option.value.toLowerCase().trim() !== permanentSearchValue.toLowerCase().trim() || option.type === "custom",
      ) &&
      permanentSearchValue.toLowerCase().trim()
    ) {
      return (
        <div className={styles.customOptionField} {...data.innerProps}>
          {addCustomOptionRenderer(permanentSearchValue)}
        </div>
      )
    }

    return <></>
  }

  const handleMenuClose = () => {
    setPermanentSearchValue("")
    setSearchValue("")
  }

  const optionRenderer = (optionProps: any) => {
    if (optionProps.data.type === "custom") {
      return renderCustomOption(optionProps)
    }
    return <components.Option {...optionProps} />
  }

  const options = useMemo(() => {
    if (responseType === "stringArray") {
      return [
        ...convertStringArrayToOptions(data?.data || []),
        ...(addCustomOptionRenderer && searchValue !== ""
          ? [{ value: permanentSearchValue, label: permanentSearchValue, type: "custom" }]
          : []),
      ]
    }

    // @ts-expect-error this happens because data has any type
    return convertDataToOptions(data, valueName, labelName)
  }, [data, searchValue, addCustomOptionRenderer, permanentSearchValue, responseType])

  return (
    <SelectStatic
      label={label}
      dataTestId={dataTestId}
      options={options}
      isLoading={isFetching}
      placeholder={placeholder}
      name={name}
      className={classNames("custom", className, {
        [styles.error]: errorMessage,
      })}
      errorMessage={errorMessage}
      values={values?.length ? values : []}
      onSearchChange={searchFn ? onSearchChange : undefined}
      onChange={onSelectChange}
      disabled={disabled}
      backspaceDelete={backspaceDelete}
      onMenuClose={handleMenuClose}
      optionRenderer={optionRenderer}
      onInputChange={handleInputChange}
      noOptionsMessage={noOptionsMessage}
      isSearchable={isSearchable}
    />
  )
}
